import { Link } from 'react-router-dom'

import { Button, Notice } from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { instagramLoginChannelsUrl } from '~publish/helpers/channelHighlight'
import { isInstagramLoginChannel } from '~publish/helpers/channels'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { FeedRow } from '~publish/pages/Community/components/FeedRow'

import { openInstagramFacebookReAuthModal } from '../../util/instagramFacebookReAuth'

import styles from './MentionsInstagramLoginNotice.module.css'

import type { JSX } from 'react'

const BANNER_ID = 'community-mentions-instagram-login-notice'

export type MentionsInstagramLoginNoticeChannel = {
  id: string
  service: string
  type: string
  name: string
  displayName?: string | null
  apiVersion?: string | null
}

type MentionsInstagramLoginNoticeProps = {
  channels: readonly MentionsInstagramLoginNoticeChannel[]
}

function buildMessage(
  channels: readonly MentionsInstagramLoginNoticeChannel[],
): string {
  const names = channels.map(
    (channel) => `‘${getChannelNameForDisplay(channel)}’`,
  )
  const nameList =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  const target =
    names.length === 1
      ? 'your Instagram account to a Facebook Page'
      : 'your Instagram accounts to Facebook Pages'

  return `To access Instagram mentions for ${nameList}, link ${target}.`
}

/**
 * Explains why the Mentions inbox is missing the Instagram channels connected
 * through Instagram login, and points the actor at the switch to Facebook
 * login. Mentions-only — comments work on either login.
 *
 * Renders nothing when no channel in scope is affected, so callers can mount it
 * unconditionally above the mentions feed.
 */
export function MentionsInstagramLoginNotice({
  channels,
}: MentionsInstagramLoginNoticeProps): JSX.Element | null {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const banner = useDismissableBanner(BANNER_ID, { trackDismiss: true })

  const affectedChannels = channels.filter(isInstagramLoginChannel)

  if (affectedChannels.length === 0 || !banner.isActive) {
    return null
  }

  const isSingleChannel = affectedChannels.length === 1

  return (
    <FeedRow>
      <Notice
        variant="info"
        onDismiss={banner.dismiss}
        className={styles.notice}
      >
        <Notice.Text>{buildMessage(affectedChannels)}</Notice.Text>
        {canManageChannels && (
          <Notice.Actions>
            {isSingleChannel ? (
              <Button
                size="small"
                onClick={(): void =>
                  openInstagramFacebookReAuthModal(affectedChannels[0])
                }
              >
                Link Account to Facebook Page
              </Button>
            ) : (
              <Button
                as={Link}
                size="small"
                to={instagramLoginChannelsUrl}
                className={styles.linkAction}
              >
                Go to Channels
              </Button>
            )}
          </Notice.Actions>
        )}
      </Notice>
    </FeedRow>
  )
}
